import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useCreateNpsResponse } from './useCreateNpsAnswer'

export type NpsForm = {
  scoring: number | undefined
  surveyIntentId: string | undefined
  feedback: string
  survey: string
}

export const useNpsForm = (survey: string, onSuccess?: () => void) => {
  const { create: createResponse } = useCreateNpsResponse()
  const form = useForm<NpsForm>({
    defaultValues: {
      scoring: undefined,
      surveyIntentId: undefined,
      feedback: '',
      survey,
    },
    async onSubmit(values, { setErrors }) {
      track(trackingEvents.nps.completesFeedbackStep, {
        score: values.scoring,
        survey,
      })

      const result = await createResponse(
        {
          survey,
          feedback: values.feedback,
          score: values.scoring,
          source: 'browser',
        },
        values.surveyIntentId
      )

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        track(trackingEvents.nps.submitNpsAnswer, { score: values.scoring, survey })
        onSuccess?.()
      }

      return handled
    },
    validate(values) {
      const errors: FormErrors<NpsForm> = {}

      if (!values.scoring || values.scoring > 10 || values.scoring < 1) {
        errors.scoring = i18n.t('common.validations.empty')
      }

      if (!values.survey) {
        errors.survey = i18n.t('common.validations.empty')
      }

      return errors
    },
  })

  const onChange = (field: keyof NpsForm, value: NpsForm[typeof field]) => {
    form.setFieldValue(field, value)
  }

  return {
    values: form.values,
    onChange,
    onSubmit: form.handleSubmit,
    isSubmitting: form.isSubmitting,
    isFormValid: form.isValid,
    resetForm: form.resetForm,
  }
}
